import { useCallback, useSyncExternalStore } from 'react'
import {
  getSessionAudioRecorder,
  getSessionRecordingUploader,
  subscribeSessionRecordingAdapters,
} from '../data/adapters'
import type { SessionAudioRecorder } from '../data/recorder-port'
import type { SessionRecordingUploadState, SessionRecordingUploader } from '../data/uploader-port'

/** The registered device recorder (re-renders when the native adapter registers). */
export function useSessionAudioRecorder(): SessionAudioRecorder {
  return useSyncExternalStore(subscribeSessionRecordingAdapters, getSessionAudioRecorder, getSessionAudioRecorder)
}

export function useSessionRecordingUploader(): SessionRecordingUploader {
  return useSyncExternalStore(
    subscribeSessionRecordingAdapters,
    getSessionRecordingUploader,
    getSessionRecordingUploader,
  )
}

const EMPTY_UPLOADS: ReadonlyMap<string, SessionRecordingUploadState> = new Map()
let cachedFor: SessionRecordingUploader | null = null
let cachedList: SessionRecordingUploadState[] | null = null
let cachedMap: ReadonlyMap<string, SessionRecordingUploadState> = EMPTY_UPLOADS

function uploadsSnapshot(uploader: SessionRecordingUploader): ReadonlyMap<string, SessionRecordingUploadState> {
  const list = uploader.list()
  if (cachedFor === uploader && cachedList != null && sameList(cachedList, list)) return cachedMap
  cachedFor = uploader
  cachedList = list
  cachedMap = list.length === 0 ? EMPTY_UPLOADS : new Map(list.map((entry) => [entry.recordingId, entry]))
  return cachedMap
}

function sameList(a: SessionRecordingUploadState[], b: SessionRecordingUploadState[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((entry, i) => {
    const other = b[i]
    return (
      entry.recordingId === other.recordingId &&
      entry.phase === other.phase &&
      entry.progress === other.progress &&
      entry.error === other.error &&
      entry.attempt === other.attempt &&
      entry.nextRetryAt === other.nextRetryAt
    )
  })
}

/** Live upload-queue states by recording id (from `uploader.list()` + `subscribe`). */
export function useSessionRecordingUploads(): ReadonlyMap<string, SessionRecordingUploadState> {
  const uploader = useSessionRecordingUploader()
  const subscribe = useCallback((listener: () => void) => uploader.subscribe(listener), [uploader])
  const getSnapshot = useCallback(() => uploadsSnapshot(uploader), [uploader])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
